// Audio for alarm - plays a beep pattern once
export const playAlarmBeep = () => {
    try {
        const audioContext = new (window.AudioContext ||
            (window as any).webkitAudioContext)();
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        oscillator.frequency.value = 800;
        oscillator.type = "sine";
        gainNode.gain.value = 0.3;

        oscillator.start();

        // Beep pattern
        setTimeout(() => {
            gainNode.gain.value = 0;
        }, 200);
        setTimeout(() => {
            gainNode.gain.value = 0.3;
        }, 300);
        setTimeout(() => {
            gainNode.gain.value = 0;
        }, 500);
        setTimeout(() => {
            gainNode.gain.value = 0.3;
        }, 600);
        setTimeout(() => {
            oscillator.stop();
            audioContext.close();
        }, 800);
    } catch (e) {
        console.error("Audio playback failed:", e);
    }
};

// Celebration music - plays a happy jingle
export const playCelebrationSound = () => {
    try {
        const audioContext = new (window.AudioContext ||
            (window as any).webkitAudioContext)();

        // Create multiple oscillators for a richer sound
        const playNote = (frequency: number, startTime: number, duration: number, type: OscillatorType = "sine") => {
            const oscillator = audioContext.createOscillator();
            const gainNode = audioContext.createGain();

            oscillator.connect(gainNode);
            gainNode.connect(audioContext.destination);

            oscillator.frequency.value = frequency;
            oscillator.type = type;

            // Envelope
            gainNode.gain.setValueAtTime(0, audioContext.currentTime + startTime);
            gainNode.gain.linearRampToValueAtTime(0.2, audioContext.currentTime + startTime + 0.05);
            gainNode.gain.linearRampToValueAtTime(0.15, audioContext.currentTime + startTime + duration * 0.5);
            gainNode.gain.linearRampToValueAtTime(0, audioContext.currentTime + startTime + duration);

            oscillator.start(audioContext.currentTime + startTime);
            oscillator.stop(audioContext.currentTime + startTime + duration);
        };

        // Play a celebratory arpeggio (C major chord ascending)
        // C4 - E4 - G4 - C5 with harmony
        const notes = [
            { freq: 523.25, time: 0, duration: 0.15 },     // C5
            { freq: 659.25, time: 0.12, duration: 0.15 },  // E5
            { freq: 783.99, time: 0.24, duration: 0.15 },  // G5
            { freq: 1046.50, time: 0.36, duration: 0.3 },  // C6
        ];

        // Add harmony notes (third below)
        const harmonyNotes = [
            { freq: 392.00, time: 0, duration: 0.15 },     // G4
            { freq: 523.25, time: 0.12, duration: 0.15 },  // C5
            { freq: 659.25, time: 0.24, duration: 0.15 },  // E5
            { freq: 783.99, time: 0.36, duration: 0.3 },   // G5
        ];

        // Play main melody
        notes.forEach(note => {
            playNote(note.freq, note.time, note.duration, "sine");
        });

        // Play harmony (quieter)
        harmonyNotes.forEach(note => {
            playNote(note.freq, note.time, note.duration, "triangle");
        });

        // Add a final sparkle
        setTimeout(() => {
            playNote(1318.51, 0, 0.4, "sine"); // E6
            playNote(1567.98, 0.05, 0.35, "sine"); // G6
        }, 500);

        // Close audio context after all sounds complete
        setTimeout(() => {
            audioContext.close();
        }, 1500);
    } catch (e) {
        console.error("Celebration audio playback failed:", e);
    }
};

// Repeating alarm manager
class AlarmManager {
    private intervals: Map<string, NodeJS.Timeout> = new Map();

    startAlarm(taskId: string) {
        if (this.intervals.has(taskId)) return;

        // Play immediately
        playAlarmBeep();

        // Then repeat every 3 seconds
        const interval = setInterval(() => {
            playAlarmBeep();
        }, 3000);

        this.intervals.set(taskId, interval);
    }

    stopAlarm(taskId: string) {
        const interval = this.intervals.get(taskId);
        if (interval) {
            clearInterval(interval);
            this.intervals.delete(taskId);
        }
    }

    stopAllAlarms() {
        this.intervals.forEach((interval) => clearInterval(interval));
        this.intervals.clear();
    }
}

export const alarmManager = new AlarmManager();

// Soft chime for reflection prompts - gentle bell-like sound
export const playReflectionChime = () => {
    try {
        const audioContext = new (window.AudioContext ||
            (window as any).webkitAudioContext)();

        const playChimeNote = (
            frequency: number,
            startTime: number,
            duration: number,
            volume: number = 0.15
        ) => {
            const oscillator = audioContext.createOscillator();
            const gainNode = audioContext.createGain();

            oscillator.connect(gainNode);
            gainNode.connect(audioContext.destination);

            oscillator.frequency.value = frequency;
            oscillator.type = "sine";

            // Bell-like envelope with quick attack and slow decay
            gainNode.gain.setValueAtTime(0, audioContext.currentTime + startTime);
            gainNode.gain.linearRampToValueAtTime(
                volume,
                audioContext.currentTime + startTime + 0.02
            );
            gainNode.gain.exponentialRampToValueAtTime(
                0.001,
                audioContext.currentTime + startTime + duration
            );

            oscillator.start(audioContext.currentTime + startTime);
            oscillator.stop(audioContext.currentTime + startTime + duration);
        };

        // Gentle two-note chime (like a meditation bell)
        // G5 followed by C6 - peaceful interval
        playChimeNote(783.99, 0, 1.5, 0.12); // G5
        playChimeNote(1046.5, 0.3, 1.2, 0.1); // C6

        // Add subtle harmonics for richness
        playChimeNote(1567.98, 0, 1.0, 0.04); // G6 (harmonic)
        playChimeNote(2093.0, 0.3, 0.8, 0.03); // C7 (harmonic)

        // Close audio context after sound completes
        setTimeout(() => {
            audioContext.close();
        }, 2000);
    } catch (e) {
        console.error("Reflection chime playback failed:", e);
    }
};
